import { useCallback, useEffect, useState } from 'react';
import { ASSISTANT_MODES, ASSISTANT_MODE_LIST, STORAGE_KEYS } from '../config/modes';

const isValidMode = (mode) => ASSISTANT_MODE_LIST.includes(mode);
const isPersistableMode = (mode) => isValidMode(mode) && mode !== ASSISTANT_MODES.FULLSCREEN;

const readStoredMode = () => {
    if (typeof window === 'undefined') return null;
    try {
        const stored = window.localStorage.getItem(STORAGE_KEYS.MODE);
        return isPersistableMode(stored) ? stored : null;
    } catch {
        return null;
    }
};

const writeStoredMode = (mode) => {
    if (typeof window === 'undefined') return;
    if (!isPersistableMode(mode)) return;
    try {
        window.localStorage.setItem(STORAGE_KEYS.MODE, mode);
    } catch {
        // ignore storage errors
    }
};

export function useAssistantMode({
    initialMode = ASSISTANT_MODES.FULLSCREEN,
    controlledMode,
    onModeChange,
    persist = true,
} = {}) {
    const isControlled = controlledMode !== undefined;

    const [internalMode, setInternalMode] = useState(() => {
        if (isControlled && isValidMode(controlledMode)) return controlledMode;
        if (persist) {
            const stored = readStoredMode();
            if (stored) return stored;
        }
        return isValidMode(initialMode) ? initialMode : ASSISTANT_MODES.FULLSCREEN;
    });

    useEffect(() => {
        if (isControlled && isValidMode(controlledMode) && controlledMode !== internalMode) {
            setInternalMode(controlledMode);
        }
    }, [isControlled, controlledMode, internalMode]);

    const setMode = useCallback(
        (next) => {
            if (!isValidMode(next)) return;
            if (!isControlled) {
                setInternalMode(next);
                if (persist) writeStoredMode(next);
            }
            onModeChange?.(next);
        },
        [isControlled, persist, onModeChange],
    );

    return [internalMode, setMode];
}
